<template>
  <v-container fluid>
    <v-container class="d-flex justify-space-between align-center" fluid>
      최종 갱신: {{ lastUpdate.toLocaleString() }}
      <v-spacer></v-spacer>
      <v-btn icon @click="export2csv"><v-icon>mdi-download</v-icon></v-btn>
      <v-btn icon @click="fetchDriverTrips"><v-icon>mdi-refresh</v-icon></v-btn>
    </v-container>

    <v-alert type="info">
      전일 자료부터 확인하실 수 있습니다.
    </v-alert>

    <v-row>
      <v-col cols="12" sm="6">
        <date-picker
          v-model="selectedDate"
          label="조회 날짜"
          hide-details
          outlined></date-picker>
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          v-model="search"
          label="검색"
          hide-details
          outlined></v-text-field>
      </v-col>
    </v-row>

    <v-data-table
      :items="items"
      :headers="headers"
      :search="search"
      :custom-filter="searchByPlateNo"
      :loading="loading"
      class="text-no-wrap"
    >
      <template v-slot:item.runTime="{ item }">
        {{ item.runTime | timeformat }}
      </template>
      <template v-slot:item.restTime="{ item }">
        {{ item.restTime | timeformat }}
      </template>
      <template v-slot:item.fuelType="{ item }">
        <v-chip dark label small :color="getColor(item.fuelType)">{{ item.fuelType }}</v-chip>
      </template>
      <template v-slot:item.smartscore="{ item }">
        <v-icon small @click="showDetails(item)">mdi-information-outline</v-icon>
        {{ item.smartscore }}
      </template>
      <template v-slot:item.events="{ item }">
        <v-btn icon @click="showEvents(item)">
          <v-icon>mdi-google-maps</v-icon>
          </v-btn>
      </template>
    </v-data-table>

    <smartscore-details-dialog ref="smartscoreDetailsDialog" :item="selectedItem"></smartscore-details-dialog>
  </v-container>
</template>

<script>
import { getDriverTripList } from '@/api'
import get from 'lodash/get'
import groupBy from 'lodash/groupBy'
import export2csv from '@/utils/export2csv'

import smartscoreDetailsDialog from './smartscore-details-dialog'

export default {
  components: {
    smartscoreDetailsDialog
  },
  data () {
    return {
      lastUpdate: new Date(),
      driverTrips: [],
      loading: false,
      selectedDate: this.$moment().add(-1, 'day').format('YYYY-MM-DD'),
      selectedItem: null,
      selectedEvent: null,
      search: '',
      headers: [
        {
          text: '운전자',
          value: 'driver'
        },
        {
          text: '운행거리 (Km)',
          value: 'trip'
        },
        {
          text: '운행시간',
          value: 'runTime'
        },
        {
          text: '휴식시간',
          value: 'restTime'
        },
        {
          text: '연비 (Km)',
          value: 'fuelEfficiency',
          tooltip: '단위연료당'
        },
        {
          text: '스마트운전 점수',
          value: 'smartscore'
        }
      ]
    }
  },
  computed: {
    filters () {
      const filters = {
        date: this.$moment(this.selectedDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
      }

      return filters
    },
    query () {
      return {
        fields: '-company -companyName -group -driver -start -end',
        sort: '-startTime'
      }
    },
    items () {
      const items = this.driverTrips.map(driverTrip => {
        // const date = this.$moment(get(driverTrip, 'ymd'), 'YYMMDD').format('YYYY. MM. DD.')
        const trip = get(driverTrip, 'trip', 0)
        const fuel = get(driverTrip, 'fuel', 0)
        // const fuelEfficiency = (trip / fuel || 0)
        // const start = this.$moment(driverTrip.startTime).format('HH:mm:ss')
        // const end = this.$moment(driverTrip.endTime).format('HH:mm:ss')
        const runTime = get(driverTrip, 'length', 0)
        const restTime = get(driverTrip, 'restTime', 0)
        const smartscore = get(driverTrip, 'smartScore', '미집계')

        const rapidAcceleration = get(driverTrip, 'events.rapidAcceleration', 0)
        const rapidDeceleration = get(driverTrip, 'events.rapidDeceleration', 0)
        const rapidChangeRoute = get(driverTrip, 'events.rapidChangeRoute', 0)
        const rapidTurn = get(driverTrip, 'events.rapidTurn', 0)
        const smartscoreDetails = {
          rapidAcceleration: {
            count: rapidAcceleration,
            score: (rapidAcceleration * 0.18 / trip * 100) * -1
          },
          rapidDeceleration: {
            count: rapidDeceleration,
            score: (rapidDeceleration * 0.264 / trip * 100) * -1
          },
          rapidChangeRoute: {
            count: rapidChangeRoute,
            score: (rapidChangeRoute * 0.245 / trip * 100) * -1
          },
          rapidTurn: {
            count: rapidTurn,
            score: (rapidTurn * 0.245 / trip * 100) * -1
          }
        }

        return {
          _id: driverTrip._id,
          driver: driverTrip.driverName || '불명',
          trip,
          fuel,
          runTime,
          restTime,
          smartscore: typeof smartscore === 'number' ? smartscore.toFixed(2) : smartscore,
          smartscoreDetails
        }
      })

      const groupByDriver = groupBy(items, 'driver')

      return Object.entries(groupByDriver)
        .map(([driver, data]) => {
          const reduce = data.reduce((obj, item) => {
            obj.trip += Number(item.trip)
            obj.runTime += Number(item.runTime)
            obj.restTime += Number(item.restTime)
            obj.fuel += Number(item.fuel)
            obj.smartscore += Number(item.smartscore)
            if (!obj.smartscoreDetails) {
              obj.smartscoreDetails = item.smartscoreDetails
            } else {
              obj.smartscoreDetails.rapidAcceleration.count += item.smartscoreDetails.rapidAcceleration.count
              obj.smartscoreDetails.rapidAcceleration.score += item.smartscoreDetails.rapidAcceleration.score
              obj.smartscoreDetails.rapidDeceleration.count += item.smartscoreDetails.rapidDeceleration.count
              obj.smartscoreDetails.rapidDeceleration.score += item.smartscoreDetails.rapidDeceleration.score
              obj.smartscoreDetails.rapidChangeRoute.count += item.smartscoreDetails.rapidChangeRoute.count
              obj.smartscoreDetails.rapidChangeRoute.score += item.smartscoreDetails.rapidChangeRoute.score
              obj.smartscoreDetails.rapidTurn.count += item.smartscoreDetails.rapidTurn.count
              obj.smartscoreDetails.rapidTurn.score += item.smartscoreDetails.rapidTurn.score
            }
            return obj
          }, {
            trip: 0,
            runTime: 0,
            restTime: 0,
            fuel: 0,
            smartscore: 0,
            smartscoreDetails: null
          })
          return {
            driver: driver,
            ...reduce,
            trip: reduce.trip.toFixed(2),
            smartscore: (reduce.smartscore / data.length).toFixed(2) || 0,
            fuelEfficiency: (reduce.trip / reduce.fuel || 0).toFixed(2)
          }
        })
    }
  },
  filters: {
    timeformat (seconds) {
      const hour = parseInt(seconds / 60 / 60, 10).toString().padStart(2, '0')
      const minute = parseInt((seconds / 60) % 60).toString().padStart(2, '0')
      const second = parseInt(seconds % 60).toString().padStart(2, '0')

      return `${hour}:${minute}:${second}`
    }
  },
  watch: {
    filters: {
      immediate: true,
      handler: 'fetchDriverTrips'
    }
  },
  methods: {
    async fetchDriverTrips () {
      if (this.loading) return
      this.loading = true

      try {
        const filters = this.filters
        const query = this.query

        const { drivertrips } = await getDriverTripList(filters, query)
        this.driverTrips = drivertrips
        this.lastUpdate = new Date()
      } catch (err) {
        console.error(err)
      }

      this.loading = false
    },
    searchByPlateNo (value, search, item) {
      return value !== null && search !== null && typeof value === 'string' && value.indexOf(search) !== -1
    },
    getColor (fuelType) {
      const symbol = {
        '미지정': 'grey',
        '경유': 'light-green',
        'CNG': 'light-blue'
      }

      return symbol[fuelType] || 'grey'
    },
    showDetails (item) {
      this.selectedItem = item
      this.$refs.smartscoreDetailsDialog.show()
    },
    showEvents (item) {
      // this.selectedEvent = item
      this.$refs.eventsMapDialog.show(item)
    },
    timeformat (seconds) {
      const hour = parseInt(seconds / 60 / 60, 10).toString().padStart(2, '0')
      const minute = parseInt((seconds / 60) % 60).toString().padStart(2, '0')
      const second = parseInt(seconds % 60).toString().padStart(2, '0')

      return `${hour}:${minute}:${second}`
    },
    export2csv () {
      const options = {
        filename: '운전자별 스마트 운전 통계',
        fields: [
          {
            label: '운전자',
            value: 'driver'
          },
          {
            label: '운행거리 (Km)',
            value: 'trip'
          },
          {
            label: '운행시간',
            value: 'runTime'
          },
          {
            label: '휴식시간',
            value: 'restTime'
          },
          {
            label: '연비 (Km)',
            value: 'fuelEfficiency'
          },
          {
            label: '스마트운전 점수',
            value: 'smartscore'
          },
          {
            label: '급가속 횟수',
            value: 'smartscoreDetails.rapidAcceleration.count'
          },
          {
            label: '급제동 횟수',
            value: 'smartscoreDetails.rapidDeceleration.count'
          },
          {
            label: '급진로변경 횟수',
            value: 'smartscoreDetails.rapidChangeRoute.count'
          },
          {
            label: '급회전 횟수',
            value: 'smartscoreDetails.rapidTurn.count'
          }
        ]
      }

      const data = this.items.map(row => {
        return {
          ...row,
          runTime: this.timeformat(row.runTime),
          restTime: this.timeformat(row.restTime || 0)
        }
      })

      export2csv(data, options)
    }
  }
}
</script>
